/**
 * API client for the Learn-content translation review queue
 * (feat-018-learn-i18n, Phase 2).
 *
 * Backs the manager/corporate review surface: drain the `fresh` Spanish queue
 * (approve / inline-edit / reject), and set per-tenant / per-location language
 * defaults.
 *
 * Backend: routes/admin_translations.py (GET/PUT shipped Phase 0; DELETE added
 * Phase 2 per ADR 0004 D1) and routes/tenant_management.py (default_language
 * field on the existing tenant/location PUTs per ADR 0004 D2).
 *
 * Contract shapes are pinned in decisions/0004-phase2-frontend-review-queue.md §D6.
 *
 * SECURITY (ADR 0004 D4 / C5b): `translated_text` and `canonical_text` are
 * UNTRUSTED (LLM-generated or author-supplied). Consumers MUST render them as
 * plain text — never via dangerouslySetInnerHTML, innerHTML, markdown-to-HTML,
 * or attribute interpolation.
 */

import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Types — mirror routes/admin_translations.py + services/content_translation.py
// ============================================================

/** Canonical-entity kinds that carry translatable instructional fields
 *  (TRANSLATABLE_FIELDS keys in the backend, ADR 0001 D3). */
export type TranslationEntityType =
  | 'learn_lesson'
  | 'learn_drill'
  | 'question_item'
  | 'rehearsal_template'
  | 'rehearsal_criterion'
  // feat-230: trivia sets/questions translate through the SAME pipeline and land
  // in this queue. Without these two members the queue's type filter cannot
  // select them and `entityTypeLabel` falls through to the raw snake_case key —
  // the rows are there, just unfindable and unlabelled.
  | 'trivia_set'
  | 'trivia_question';

/** Only `fresh` and `approved` exist — there is no `rejected` (reject = DELETE,
 *  ADR 0004 D1). */
export type TranslationStatus = 'fresh' | 'approved';

/** One translated field of one canonical entity, as returned by
 *  GET /api/admin/translations (ADR 0004 D6). */
export interface TranslationRow {
  id: string;
  entity_type: TranslationEntityType;
  entity_id: string;
  field_name: string;
  language_code: string;
  /** Untrusted — render as plain text only (C5b). */
  translated_text: string;
  /** Canonical English value for the side-by-side diff. Untrusted — plain text
   *  only (C5b). May be null if the canonical row/field has vanished. */
  canonical_text: string | null;
  status: TranslationStatus;
  /** Server-derived: source_version != sha256(current canonical) (ADR 0001 D2).
   *  The client renders this; it never recomputes the hash (metrics_single_source). */
  stale: boolean;
  source_version: string | null;
  model: string | null;
  reviewed_by: string | null;
  created: string | null;
  updated: string | null;
}

export interface ListTranslationsFilters {
  /** Language subtag, e.g. 'es'. Omit for all languages. */
  language?: string;
  status?: TranslationStatus;
  entityType?: TranslationEntityType;
}

// ============================================================
// API
// ============================================================

/**
 * Translation review queue for one tenant. Each row carries its canonical
 * English value (`canonical_text`) and a server-derived `stale` flag.
 */
export async function listTranslations(
  tenantId: string,
  filters: ListTranslationsFilters = {}
): Promise<TranslationRow[]> {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters.language) params.set('language', filters.language);
  if (filters.status) params.set('status', filters.status);
  if (filters.entityType) params.set('entity_type', filters.entityType);

  const data = await fetchApi<{ translations: TranslationRow[] }>(
    `/api/admin/translations?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return data.translations ?? [];
}

export interface UpdateTranslationResult {
  status: string;
  id: string;
  new_status: TranslationStatus;
  reviewed_by: string | null;
}

/**
 * Approve and/or inline-edit a translation. Omitting `status` defaults to
 * `'approved'` on the backend (the common reviewer action). Saving an edited
 * `translated_text` therefore also approves it unless `status` is set explicitly.
 */
export async function updateTranslation(
  id: string,
  body: { translated_text?: string; status?: TranslationStatus }
): Promise<UpdateTranslationResult> {
  return fetchApi<UpdateTranslationResult>(`/api/admin/translations/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body,
  });
}

/**
 * Reject a translation by deleting the row (ADR 0004 D1). The lang-aware read
 * path then falls back to canonical English for that field.
 *
 * NOTE: the backend DELETE endpoint ships in the Phase 2 backend slice; calls
 * will 404/405 until it lands.
 */
export async function deleteTranslation(
  id: string
): Promise<{ status: string; id: string; deleted: boolean }> {
  return fetchApi<{ status: string; id: string; deleted: boolean }>(
    `/api/admin/translations/${id}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
}

/**
 * Read a tenant's current default language (ADR 0004 D2). There is no
 * `GET /api/tenant/{id}` route — the only tenant GET is the list endpoint
 * `GET /api/tenant` (role-filtered to the caller's tenant; admins get all),
 * whose rows carry `tenant_id` + `default_language` (TenantResponse). We fetch
 * the list and pick the matching row — mirroring how LocationAdminView resolves
 * a single location from `getLocationsByTenant`.
 *
 * Falls back to 'en' (the column default) if the tenant isn't in the list or
 * the field is absent. Used to PRE-POPULATE the tenant default selector so a
 * reviewer never sees — and cannot silently save — a value they didn't choose.
 */
export async function getTenantDefaultLanguage(
  tenantId: string
): Promise<string> {
  const tenants = await fetchApi<
    Array<{ tenant_id: string; default_language?: string | null }>
  >('/api/tenant', { headers: getAuthHeaders() });
  const match = (tenants ?? []).find(
    (t) => String(t.tenant_id) === String(tenantId)
  );
  return match?.default_language ?? 'en';
}

/**
 * Set a tenant's default language (ADR 0004 D2 — extends the existing
 * PUT /api/tenant/{id} with a `default_language` field).
 *
 * NOTE: the `default_language` field ships in the Phase 2 backend slice; until
 * then the value is silently ignored by the tenant update handler.
 */
export async function setTenantDefaultLanguage(
  tenantId: string,
  defaultLanguage: string
): Promise<unknown> {
  return fetchApi(`/api/tenant/${tenantId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: { default_language: defaultLanguage },
  });
}

/**
 * Set a location's default language (ADR 0004 D2 — extends the existing
 * PUT /api/tenant/{tid}/locations/{lid} with a `default_language` field).
 *
 * NOTE: the `default_language` field ships in the Phase 2 backend slice; until
 * then the value is silently ignored by the location update handler.
 */
export async function setLocationDefaultLanguage(
  tenantId: string,
  locationId: string,
  defaultLanguage: string
): Promise<unknown> {
  return fetchApi(`/api/tenant/${tenantId}/locations/${locationId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: { default_language: defaultLanguage },
  });
}

// ============================================================
// Per-tenant translation languages (feat-260, ADR 0001 §7.3/§7.4)
// ============================================================

/**
 * A tenant's explicit translation-language list, plus the universe it is
 * chosen from. Shape is pinned by ADR 0001 §7.3 (`TranslationLanguagesResponse`)
 * and is IDENTICAL for GET and PUT — the PUT response is re-SELECTed from the
 * database, never echoed from the request.
 *
 * `supported_languages` is `sorted(SUPPORTED_LOCALES - {'en'})` — `["es"]` today.
 * It is the CHECKBOX UNIVERSE (ADR 0001 §7.4). The card must render one checkbox
 * per entry in this array and must NOT hard-code `['es']`: when the backend
 * widens SUPPORTED_LOCALES the new language appears with no frontend deploy.
 * The client-side `SUPPORTED_LANGUAGES` below supplies only the human labels,
 * via `languageLabel()`.
 *
 * `translation_languages` is the tenant's current selection — sorted, deduped,
 * and `en`-free (English is never a translation target, spec AT6).
 */
export interface TenantTranslationLanguages {
  tenant_id: string;
  translation_languages: string[];
  supported_languages: string[];
}

/**
 * Read the tenant's translation-language list (feat-260, ADR 0001 §7.3).
 *
 * Unlike `getTenantDefaultLanguage` above — which has to fetch the tenant LIST
 * because no by-id tenant GET exists — this is a real by-id child route, so it
 * is a direct GET.
 *
 * THROWS on failure, and the caller MUST distinguish that from an empty list.
 * A rejected GET leaves the card all-unchecked, which is indistinguishable from
 * a tenant that deliberately translates into nothing (a legitimate config, spec
 * AT2/AT4) — and one Save away from writing that empty list for real, turning
 * translation off. That is this feature's original bug re-created through the UI
 * built to prevent it. See spec AT20 / ADR 0001 §7.4 / `design/states.md` §2b:
 * on rejection the card shows an error that outranks the empty-list note AND
 * disables Save. Do not add a `catch` here that returns an empty shape — that
 * would erase the distinction at the one layer that can still see it.
 *
 * NOTE (deploy skew): backend and frontend deploy on separate pipelines, so this
 * WILL reject in production for the window between the two. The load-failed
 * state is a normal operating state of this card, not an edge case.
 */
export async function getTenantTranslationLanguages(
  tenantId: string
): Promise<TenantTranslationLanguages> {
  return fetchApi<TenantTranslationLanguages>(
    `/api/tenant/${tenantId}/translation-languages`,
    { headers: getAuthHeaders() }
  );
}

/**
 * Replace the tenant's translation-language list (feat-260, ADR 0001 §7.3).
 *
 * Set-replace semantics: the sent array IS the new list. An empty array is a
 * REAL, supported user action ("translate into nothing", spec AT4) and gets its
 * own backend branch — it is not a no-op and not an error.
 *
 * `translation_languages` is ALWAYS present in the body, including when empty.
 * The backend model is `List[str]` with no default (ADR 0001 §7.3), so omitting
 * the key is a 422 rather than a silent clear — deliberately, because a silent
 * clear is the failure this whole feature exists to prevent. Hence the explicit
 * `?? []` rather than letting an undefined argument drop the key.
 *
 * Returns the SERVER's set, re-SELECTed from the database — never the request
 * echoed back. Render THAT, not the checkbox state that was submitted: the
 * server sorts, dedupes and drops `en`, and the card must show what was actually
 * stored (ADR 0001 §7.4, `design/states.md` §3).
 *
 * Note the asymmetry the card has to implement, which is not this function's
 * business but is the reason the return value matters: on SUCCESS render the
 * response; on FAILURE keep the admin's unsaved selection and do not revert
 * (spec AT10, `design/states.md` §7).
 */
export async function setTenantTranslationLanguages(
  tenantId: string,
  languages: string[]
): Promise<TenantTranslationLanguages> {
  return fetchApi<TenantTranslationLanguages>(
    `/api/tenant/${tenantId}/translation-languages`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: { translation_languages: [...(languages ?? [])] },
    }
  );
}

// ============================================================
// Display helpers (presentation only — no metric/business logic)
// ============================================================

/** Human-readable entity-type label for the queue UI. */
export const ENTITY_TYPE_LABELS: Record<TranslationEntityType, string> = {
  learn_lesson: 'Unit',
  learn_drill: 'Lesson',
  question_item: 'Question',
  rehearsal_template: 'Rehearsal',
  rehearsal_criterion: 'Rehearsal criterion',
  trivia_set: 'Trivia set',
  trivia_question: 'Trivia question',
};

/** Human-readable field-name labels (per the ADR 0001 D3 allow-list). */
export const FIELD_NAME_LABELS: Record<string, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  name: 'Name',
  scenario_text: 'Scenario',
  question_text: 'Question',
  option_a: 'Option A',
  option_b: 'Option B',
  option_c: 'Option C',
  option_d: 'Option D',
  explanation: 'Wrong-answer explanation',
  correct_explanation: 'Correct-answer praise',
  // feat-230 — trivia's post-answer fun fact.
  reveal_text: 'Reveal text',
  label: 'Label',
  description: 'Description',
};

export function entityTypeLabel(t: string): string {
  return ENTITY_TYPE_LABELS[t as TranslationEntityType] ?? t;
}

export function fieldNameLabel(f: string): string {
  return FIELD_NAME_LABELS[f] ?? f;
}

/** Languages offered in selectors. The schema is N-language by design; the
 *  current rollout ships en + es (ADR 0004 D2 — do not hard-restrict the column,
 *  this list is presentation only). */
export const SUPPORTED_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
];

export function languageLabel(code: string): string {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.label ?? code;
}
